import Header from "@/components/Header";
import Footer from "@/components/Footer";

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <Header />
      
      <div className="container mx-auto px-6 py-24">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-8">Privacy Policy</h1>
          <div className="prose prose-invert prose-lg max-w-none">
            <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-lg p-8 space-y-6">
              
              <section>
                <h2 className="text-2xl font-semibold text-white mb-4">1. Information We Collect</h2>
                <p className="text-gray-300 leading-relaxed">
                  At The Bulk Image Generator, we collect information you provide directly to us when using our AI-powered bulk image generation platform. This includes your email address, account information, image generation prompts, uploaded reference images, generated AI images, and any feedback or support requests you submit to us.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-white mb-4">2. How We Use Your Information</h2>
                <p className="text-gray-300 leading-relaxed">
                  We use the information we collect to:
                </p>
                <ul className="text-gray-300 list-disc pl-6 space-y-2">
                  <li>Process your bulk AI image generation requests and deliver your created content</li>
                  <li>Maintain and improve our AI models and image generation algorithms</li>
                  <li>Manage your account, subscription, and billing information</li>
                  <li>Provide customer support and respond to your inquiries</li>
                  <li>Send important updates about our service and new features</li>
                  <li>Monitor usage patterns to optimize our bulk processing capabilities</li>
                  <li>Ensure compliance with our terms of service and prevent misuse</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-white mb-4">3. AI Model Training and Your Content</h2>
                <p className="text-gray-300 leading-relaxed">
                  Your prompts and generated images may be used to improve our AI models and services. However, we do not use your personal information or proprietary content for training without your explicit consent. All image generation is processed securely, and we implement measures to protect your creative work and intellectual property.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-white mb-4">4. Information Sharing and Disclosure</h2>
                <p className="text-gray-300 leading-relaxed">
                  We do not sell, trade, or otherwise transfer your personal information or generated images to third parties without your consent, except as described in this policy. We may share information with trusted service providers who assist us in operating our platform, processing payments, or delivering our AI image generation services. All such parties are bound by confidentiality agreements.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-white mb-4">5. Data Security and Storage</h2>
                <p className="text-gray-300 leading-relaxed">
                  We implement industry-standard security measures to protect your personal information and generated images. This includes encryption in transit and at rest, secure cloud storage, regular security audits, and access controls. Your generated images are stored securely and are only accessible to you through your account.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-white mb-4">6. Data Retention and Deletion</h2>
                <p className="text-gray-300 leading-relaxed">
                  We retain your account information and generated images for as long as your account is active or as needed to provide services. You may delete individual images or request account deletion at any time through your account settings. Upon account deletion, we will remove your personal information and generated content within 30 days, except where required by law.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-white mb-4">7. Your Rights and Controls</h2>
                <p className="text-gray-300 leading-relaxed">
                  You have the right to access, update, download, or delete your personal information and generated images. You can manage your account settings, download your images in bulk, and control sharing preferences through your dashboard. For additional requests or concerns, contact us at support@aigeneratedbulkimages.com.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-white mb-4">8. Cookies and Analytics</h2>
                <p className="text-gray-300 leading-relaxed">
                  We use cookies and similar technologies to enhance your experience, analyze usage patterns, and improve our bulk image generation platform. You can control cookie settings through your browser preferences. We may use analytics tools to understand how users interact with our AI image generation features.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-white mb-4">9. Third-Party Integrations</h2>
                <p className="text-gray-300 leading-relaxed">
                  Our platform may integrate with third-party services for payment processing, cloud storage, or AI model enhancement. These integrations are governed by their respective privacy policies. We carefully select partners who maintain high privacy and security standards compatible with our commitments to you.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-white mb-4">10. Children's Privacy</h2>
                <p className="text-gray-300 leading-relaxed">
                  The Bulk Image Generator is not intended for use by children under 13 years of age. We do not knowingly collect personal information from children under 13. If we become aware that we have collected personal information from a child under 13, we will take steps to delete such information promptly.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-white mb-4">11. Changes to This Privacy Policy</h2>
                <p className="text-gray-300 leading-relaxed">
                  We may update this privacy policy to reflect changes in our practices, technology, or legal requirements. We will notify users of any material changes by posting the updated policy on this page and updating the "Last Updated" date. Continued use of our service after changes constitute acceptance of the updated policy.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-white mb-4">12. Contact Information</h2>
                <p className="text-gray-300 leading-relaxed">
                  If you have any questions about this privacy policy, our data practices, or your rights regarding your personal information and generated images, please contact us:
                </p>
                <div className="text-gray-300 mt-4 space-y-2">
                  <p><strong>Email:</strong> support@aigeneratedbulkimages.com</p>
                  <p><strong>Platform:</strong> The Bulk Image Generator</p>
                  <p><strong>Service:</strong> AI-Powered Bulk Image Generation</p>
                </div>
                <p className="text-gray-300 leading-relaxed mt-4">
                  We are committed to protecting your privacy and will respond to your inquiries promptly. For urgent privacy concerns, please include "Privacy Request" in your email subject line.
                </p>
              </section>

              <div className="border-t border-white/10 pt-6 mt-8">
                <p className="text-gray-400 text-sm">
                  Last Updated: December 2024 • The Bulk Image Generator Privacy Policy
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
